import { useState, useEffect, useRef } from 'react';
import { Position, PositionAnalysis, DEFAULT_SELL_TIERS } from '@/types/portfolio';
import { fetchMultipleStockPrices } from '@/lib/stockApi';
import { analyzePosition, calculatePortfolioStats } from '@/lib/portfolioAnalysis';
import { fetchExchangeRates, ExchangeRates, convertToCHF } from '@/lib/currencyApi';
import { PortfolioStats } from '@/components/PortfolioStats';
import { PositionCard } from '@/components/PositionCard';
import { StockTable } from '@/components/StockTable';
import { StockScorecard } from '@/components/StockScorecard';
import { AddPositionDialog } from '@/components/AddPositionDialog';
import { ExcelImport } from '@/components/ExcelImport';
import { Auth } from '@/components/Auth';
import { FibonacciRetracement } from '@/components/FibonacciRetracement';
import { PortfolioGainsPieChart } from '@/components/PortfolioGainsPieChart';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { RefreshCw, TrendingUp, LogOut, ArrowUpDown, Clock, FileBarChart, Trash2, ClipboardList, Search, HelpCircle, Sparkles, Moon, Sun, BarChart3, Upload, Database, ShieldCheck } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import tradeCompassLogo from '@/assets/tradecompass-logo.png';
import { AutoSaveIndicator } from '@/components/AutoSaveIndicator';
import { AIChatWidget } from '@/components/AIChatWidget';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type SortOption = 'name' | 'totalValue' | 'gainLoss' | 'addedValue';

const Index = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [analyses, setAnalyses] = useState<PositionAnalysis[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({ USD: 0.88, EUR: 0.95, CHF: 1 });
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(3600); // 60 minutes in seconds
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'loading' | 'success' | 'mixed' | 'error'>('idle');
  const [successRate, setSuccessRate] = useState(100);
  const [fetchProgress, setFetchProgress] = useState(0);
  const { toast } = useToast();
  const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const removeDuplicates = async () => {
    if (!user) return;
    
    // Get all positions
    const { data: allPositions } = await supabase
      .from('positions')
      .select('*')
      .order('created_at', { ascending: true });
    
    if (!allPositions) return;
    
    // Group by ticker and keep only the first one
    const seen = new Set<string>();
    const duplicateIds: string[] = [];
    
    allPositions.forEach(pos => {
      if (seen.has(pos.ticker)) {
        duplicateIds.push(pos.id);
      } else {
        seen.add(pos.ticker);
      }
    });
    
    // Delete duplicates if any found
    if (duplicateIds.length > 0) {
      await supabase
        .from('positions')
        .delete()
        .in('id', duplicateIds);
      
      toast({
        title: "Duplicates Removed",
        description: `Removed ${duplicateIds.length} duplicate position${duplicateIds.length !== 1 ? 's' : ''}`,
      });
    }
  };

  const loadPositions = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('positions')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error Loading Positions",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    setPositions(data.map(p => ({
      id: p.id,
      ticker: (p as any).stock_name || p.ticker,
      buyPrice: Number(p.buy_price),
      quantity: p.quantity,
      totalValue: p.total_value ? Number(p.total_value) : undefined,
      localCurrency: p.local_currency || undefined,
      gainPct: p.gain_pct ? Number(p.gain_pct) : undefined,
      addedValueChf: p.added_value_chf ? Number(p.added_value_chf) : undefined,
      pricePerShare: p.price_per_share ? Number(p.price_per_share) : undefined,
      isin: p.isin || undefined,
      stockCode: p.ticker,
      initialInvestment: (p as any).initial_investment ? Number((p as any).initial_investment) : undefined,
    })));
  };

  useEffect(() => {
    const initializeData = async () => {
      await removeDuplicates();
      await loadPositions();
    };
    
    initializeData();
    
    // Fetch exchange rates on mount and every hour
    const loadRates = async () => {
      const rates = await fetchExchangeRates();
      setExchangeRates(rates);
    };
    
    loadRates();
    const interval = setInterval(loadRates, 60 * 60 * 1000); // Every hour
    
    return () => clearInterval(interval);
  }, [user]);
  
  const fetchPrices = async () => {
    if (positions.length === 0) {
      setAnalyses([]);
      return;
    }
    
    setLoading(true);
    setRefreshStatus('loading');
    setFetchProgress(0);
    try {
      // IG Markets API disabled - using Excel data only
      const prices = new Map(); // Empty price map
      setFetchProgress(100);
      
      let successCount = 0;
      let totalCount = positions.length;
      
      const newAnalyses = positions.map(position => {
        const symbolToUse = position.stockCode || position.ticker;
        const cleanedTicker = symbolToUse
          .replace(/\s*\(.*?\)\s*/g, '')
          .replace(/\s*\|.*$/g, '')
          .replace(/\s*-[A-Z]-\s*/g, '')
          .replace(/\s+/g, '')
          .toUpperCase()
          .trim();
        
        const priceData = prices.get(cleanedTicker);
        const currentPrice = priceData?.price && priceData.price > 0 
          ? priceData.price 
          : (position.pricePerShare || position.buyPrice);
        
        // Count successful fetches
        if (priceData?.price && priceData.price > 0) {
          successCount++;
        }
        
        // Create analysis with Excel data if available
        const analysis = analyzePosition(position, currentPrice, exchangeRates);
        
        // If Excel data provides gainPct, recalculate recovery metrics
        let finalAnalysis = analysis;
        if (position.gainPct !== undefined) {
          const gainPctValue = position.gainPct * 100; // Convert decimal to percentage
          const investedValue = position.buyPrice * position.quantity;
          
          // Find the highest tier that qualifies
          let cumulativeRecoverPct = 0;
          let amountToRecover = 0;
          let matchedTier = null;
          
          for (const tier of DEFAULT_SELL_TIERS.sort((a, b) => b.gainPct - a.gainPct)) {
            if (gainPctValue >= tier.gainPct) {
              matchedTier = tier;
              cumulativeRecoverPct = tier.cumulativeRecoverPct;
              amountToRecover = investedValue * (cumulativeRecoverPct / 100);
              break;
            }
          }
          
          // Calculate shares to sell - use direct percentage if specified
          let recommendedSellQty = 0;
          if (matchedTier?.sellQtyPct !== undefined) {
            recommendedSellQty = Math.floor(position.quantity * (matchedTier.sellQtyPct / 100));
          } else if (amountToRecover > 0) {
            recommendedSellQty = Math.floor(amountToRecover / currentPrice);
          }
          
          const recommendedSellPct = position.quantity > 0
            ? Math.round((recommendedSellQty / position.quantity) * 100)
            : 0;
          
          const currency = position.localCurrency || 'CHF';
          const capitalRecovered = amountToRecover;
          const capitalRecoveredCHF = convertToCHF(capitalRecovered, currency, exchangeRates);
          const remainingCapitalAtRisk = Math.max(0, investedValue - capitalRecovered);
          
          finalAnalysis = {
            ...analysis,
            gainPct: position.gainPct,
            recommendedSellQty,
            recommendedSellPct,
            capitalRecovered: Math.round(capitalRecovered * 100) / 100,
            capitalRecoveredCHF: Math.round(capitalRecoveredCHF * 100) / 100,
            capitalRecoveredPct: cumulativeRecoverPct,
            remainingCapitalAtRisk: Math.round(remainingCapitalAtRisk * 100) / 100
          };
        }
        
        // Override with Excel data if present
        return {
          ...finalAnalysis,
          totalValue: position.totalValue || finalAnalysis.totalValue,
          localCurrency: position.localCurrency,
          gainAmount: position.gainPct !== undefined 
            ? (position.totalValue || finalAnalysis.totalValue) * position.gainPct 
            : finalAnalysis.gainAmount,
          addedValueChf: position.addedValueChf,
          pricePerShare: position.pricePerShare,
          currentPrice,
          isin: position.isin,
          stockCode: position.stockCode
        };
      });
      
      // Calculate success rate
      const rate = (successCount / totalCount) * 100;
      setSuccessRate(rate);
      
      // Set status based on success rate
      if (rate === 100) {
        setRefreshStatus('success');
      } else if (rate === 0) {
        setRefreshStatus('error');
      } else {
        setRefreshStatus('mixed');
      }
      
      setAnalyses(newAnalyses);
      toast({
        title: "Data Loaded",
        description: `Using Excel data for ${totalCount} positions`,
      });
      
      // Reset status after 3 seconds
      setTimeout(() => setRefreshStatus('idle'), 3000);
    } catch (error) {
      setRefreshStatus('error');
      toast({
        title: "Error Fetching Prices",
        description: "Failed to update stock prices. Please try again.",
        variant: "destructive"
      });
      // Reset status after 3 seconds
      setTimeout(() => setRefreshStatus('idle'), 3000);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPrices();
    setTimeUntilRefresh(3600); // Reset countdown to 60 minutes
    
    // Auto-refresh prices every 60 minutes
    const interval = setInterval(() => {
      fetchPrices();
      setTimeUntilRefresh(3600); // Reset countdown
    }, 3600000); // 60 minutes for IG API updates
    
    return () => clearInterval(interval);
  }, [positions]);
  
  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeUntilRefresh((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleAddPosition = async (newPosition: Omit<Position, 'id'>) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('positions')
      .insert({
        ticker: newPosition.ticker,
        buy_price: newPosition.buyPrice,
        quantity: newPosition.quantity,
        user_id: user.id
      });

    if (error) {
      toast({
        title: "Error Adding Position",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Position Added",
      description: `Added ${newPosition.ticker} to portfolio`,
    });
  };
  
  const handleRefreshPrices = () => {
    fetchPrices();
    setTimeUntilRefresh(3600); // Reset countdown on manual refresh
  };
  
  const handleDeletePosition = async (id: string) => {
    const { error } = await supabase
      .from('positions')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error Removing Position",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Position Removed",
      description: "Position removed from portfolio",
    });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleResetData = async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from('positions')
      .delete()
      .eq('user_id', user.id);

    if (error) {
      toast({
        title: "Error Resetting Data",
        description: error.message,
        variant: "destructive"
      });
      return;
    }

    await loadPositions();
    toast({
      title: "Data Reset",
      description: "All positions have been cleared from the database",
    });
  };

  const scrollToCard = (id: string) => {
    const element = cardRefs.current[id];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect
      element.classList.add('ring-4', 'ring-primary', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-primary', 'ring-opacity-50');
      }, 2000);
    }
  };

  if (!user) {
    return <Auth />;
  }
  
  const stats = analyses.length > 0 
    ? calculatePortfolioStats(analyses, exchangeRates)
    : { totalInvested: 0, totalValue: 0, totalValueCHF: 0, totalInvestedCHF: 0, totalAddedValueCHF: 0, initialTotalInvestment: 0, totalGain: 0, totalGainPct: 0, positionCount: 0 };
  
  // Calculate alert counts by threshold (15%, 30%, 45%)
  const alertCounts = analyses.reduce((acc, analysis) => {
    const gainPct = analysis.gainPct;
    if (gainPct >= 45) acc.red++;      // 100% recovery
    else if (gainPct >= 30) acc.orange++; // 40% recovery
    else if (gainPct >= 15) acc.yellow++; // 20% recovery
    return acc;
  }, { yellow: 0, orange: 0, red: 0 });
  
  // Sort analyses based on selected option
  const sortedAnalyses = [...analyses].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.ticker.localeCompare(b.ticker);
      case 'totalValue':
        return (b.totalValue || 0) - (a.totalValue || 0);
      case 'gainLoss':
        return (b.gainPct || 0) - (a.gainPct || 0);
      case 'addedValue':
        return (b.addedValueChf || 0) - (a.addedValueChf || 0);
      default:
        return 0;
    }
  });
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-success/3 rounded-full blur-3xl" />
      </div>
      
      {/* Progress Bar */}
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border shadow-lg">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-foreground min-w-[180px]">
                Refreshing stock prices...
              </span>
              <Progress value={fetchProgress} className="flex-1" />
              <span className="text-sm font-semibold text-primary min-w-[50px] text-right">
                {fetchProgress}%
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Status Bar */}
      {!loading && refreshStatus !== 'idle' && (
        <div className={cn(
          "fixed top-0 left-0 right-0 h-1 z-50 transition-all duration-300",
          refreshStatus === 'success' && "bg-success",
          refreshStatus === 'mixed' && "bg-orange-500",
          refreshStatus === 'error' && "bg-destructive"
        )}>
          <div className="h-full transition-all duration-500" style={{ width: '100%' }} />
        </div>
      )}
      
      <div className="relative container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col gap-6 mb-10">
          {/* Top Bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-lg">
                <TrendingUp className="h-8 w-8 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground flex items-center gap-2">
                  GainTracker
                  <Sparkles className="h-5 w-5 text-accent" />
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <img 
                    src={tradeCompassLogo} 
                    alt="TradeCompass" 
                    className="h-5 w-auto opacity-80"
                  />
                  <span className="text-xs text-muted-foreground font-medium">
                    Provided by TradeCompass
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-full border border-border">
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-muted-foreground font-medium">User:</span>
                <span className="text-xs font-semibold text-foreground truncate max-w-[180px]">
                  {user?.email || 'Unknown'}
                </span>
              </div>
              <AutoSaveIndicator />
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={() => navigate('/help')} className="gap-2">
                <HelpCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Help</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="gap-2">
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
          
          {/* Navigation Cards - Large Centered Icons */}
          <div className="flex justify-center">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-6 w-full max-w-6xl">
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-primary/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={handleRefreshPrices}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-primary/10 rounded-2xl mb-3 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <RefreshCw className={cn("h-8 w-8 md:h-10 md:w-10 text-primary", loading && "animate-spin")} />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">Refresh</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-accent/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => navigate('/ibkr-import')}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-accent/10 rounded-2xl mb-3 group-hover:bg-accent/20 group-hover:scale-110 transition-all duration-300">
                    <FileBarChart className="h-8 w-8 md:h-10 md:w-10 text-accent" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">IBKR Analysis</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-success/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => navigate('/revolut-import')}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-success/10 rounded-2xl mb-3 group-hover:bg-success/20 group-hover:scale-110 transition-all duration-300">
                    <FileBarChart className="h-8 w-8 md:h-10 md:w-10 text-success" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">Revolut</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-orange-500/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => navigate('/db-order')}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-orange-500/10 rounded-2xl mb-3 group-hover:bg-orange-500/20 group-hover:scale-110 transition-all duration-300">
                    <ClipboardList className="h-8 w-8 md:h-10 md:w-10 text-orange-600 dark:text-orange-400" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">DB Order</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-purple-500/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => navigate('/sma-surveillance')}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-purple-500/10 rounded-2xl mb-3 group-hover:bg-purple-500/20 group-hover:scale-110 transition-all duration-300">
                    <BarChart3 className="h-8 w-8 md:h-10 md:w-10 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">IBKR Surveillance</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-cyan-500/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => document.getElementById('excel-import-trigger')?.click()}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-cyan-500/10 rounded-2xl mb-3 group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
                    <Upload className="h-8 w-8 md:h-10 md:w-10 text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">Excel Import</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-teal-500/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => navigate('/collected-data')}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-teal-500/10 rounded-2xl mb-3 group-hover:bg-teal-500/20 group-hover:scale-110 transition-all duration-300">
                    <Database className="h-8 w-8 md:h-10 md:w-10 text-teal-600 dark:text-teal-400" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">Collected Data</span>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-xl hover:border-emerald-500/40 hover:scale-105 transition-all duration-300 group bg-card/90 backdrop-blur-sm"
                onClick={() => navigate('/hedge-protection')}
              >
                <CardContent className="p-6 md:p-8 flex flex-col items-center justify-center text-center min-h-[140px] md:min-h-[160px]">
                  <div className="p-4 md:p-5 bg-emerald-500/10 rounded-2xl mb-3 group-hover:bg-emerald-500/20 group-hover:scale-110 transition-all duration-300">
                    <ShieldCheck className="h-8 w-8 md:h-10 md:w-10 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="text-sm md:text-base font-semibold text-foreground">Hedge Protection</span>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Platform Modules Showcase */}
          <div className="w-full max-w-6xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-border" />
              <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Platform Modules</h2>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Refresh & Portfolio */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-primary/10 hover:border-primary/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={handleRefreshPrices}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-primary/10 rounded-xl"><RefreshCw className="h-5 w-5 text-primary" /></div>
                    <div><CardTitle className="text-base">Portfolio Dashboard</CardTitle><CardDescription className="text-xs">Real-time portfolio overview</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Live stock price refresh with progress tracking</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Portfolio stats: total value, gains, capital recovery %</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Alert system with 3-tier thresholds (15%, 30%, 45%)</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Stock scorecard with sell recommendations per position</li>
                    <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span>Fibonacci retracement calculator & pie chart breakdown</li>
                  </ul>
                </CardContent>
              </Card>

              {/* IBKR Full Analysis */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-accent/10 hover:border-accent/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => navigate('/ibkr-import')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-accent/10 rounded-xl"><FileBarChart className="h-5 w-5 text-accent" /></div>
                    <div><CardTitle className="text-base">IBKR Full Analysis PF</CardTitle><CardDescription className="text-xs">Interactive Brokers portfolio import</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>PDF statement parsing for positions, trades & dividends</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Cash balances, deposits, withdrawals & fee tracking</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Financial summary with realised P&L breakdown</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Multi-currency support with CHF conversion</li>
                    <li className="flex items-start gap-2"><span className="text-accent mt-0.5">•</span>Historical import archive with cloud persistence</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Revolut */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-green-500/10 hover:border-green-500/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => navigate('/revolut-import')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-green-500/10 rounded-xl"><FileBarChart className="h-5 w-5 text-green-600 dark:text-green-400" /></div>
                    <div><CardTitle className="text-base">Revolut Import</CardTitle><CardDescription className="text-xs">Revolut trading account sync</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">•</span>Excel-based import of Revolut trading positions</li>
                    <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">•</span>Position count & portfolio value aggregation</li>
                    <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">•</span>Currency-aware value normalisation</li>
                    <li className="flex items-start gap-2"><span className="text-green-500 mt-0.5">•</span>Cloud-saved import history with timestamps</li>
                  </ul>
                </CardContent>
              </Card>

              {/* DB Order */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-orange-500/10 hover:border-orange-500/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => navigate('/db-order')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-orange-500/10 rounded-xl"><ClipboardList className="h-5 w-5 text-orange-600 dark:text-orange-400" /></div>
                    <div><CardTitle className="text-base">DB Order</CardTitle><CardDescription className="text-xs">Trading order management</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span>Create Buy/Sell orders with ISIN, exchange code & limits</li>
                    <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span>Support for Normal & Limit order types</li>
                    <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span>Auto-generated PDF per order, emailed to portfolio manager</li>
                    <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span>Order archive with status tracking & PDF download</li>
                    <li className="flex items-start gap-2"><span className="text-orange-500 mt-0.5">•</span>Multi-portfolio support (CHF, EUR, USD)</li>
                  </ul>
                </CardContent>
              </Card>

              {/* IBKR Surveillance */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-purple-500/10 hover:border-purple-500/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => navigate('/sma-surveillance')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-purple-500/10 rounded-xl"><BarChart3 className="h-5 w-5 text-purple-600 dark:text-purple-400" /></div>
                    <div><CardTitle className="text-base">IBKR Surveillance</CardTitle><CardDescription className="text-xs">SMA & Ichimoku technical analysis</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-purple-500 mt-0.5">•</span>SMA crossover detection (20/50/200-day) with Buy/Sell signals</li>
                    <li className="flex items-start gap-2"><span className="text-purple-500 mt-0.5">•</span>Ichimoku Cloud analysis with 5-component scoring</li>
                    <li className="flex items-start gap-2"><span className="text-purple-500 mt-0.5">•</span>Interactive price history charts per stock</li>
                    <li className="flex items-start gap-2"><span className="text-purple-500 mt-0.5">•</span>Batch analysis across entire portfolio</li>
                    <li className="flex items-start gap-2"><span className="text-purple-500 mt-0.5">•</span>Signal summary with scan reports via email</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Excel Import */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-cyan-500/10 hover:border-cyan-500/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => document.getElementById('excel-import-trigger')?.click()}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-cyan-500/10 rounded-xl"><Upload className="h-5 w-5 text-cyan-600 dark:text-cyan-400" /></div>
                    <div><CardTitle className="text-base">Excel Import</CardTitle><CardDescription className="text-xs">PRVFIN spreadsheet integration</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-cyan-500 mt-0.5">•</span>Direct .xlsx/.xls import from PRVFIN tab</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-500 mt-0.5">•</span>Configurable row range (default 15–157)</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-500 mt-0.5">•</span>Auto currency detection & live exchange rate conversion</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-500 mt-0.5">•</span>Preview with position count & CHF grand total</li>
                    <li className="flex items-start gap-2"><span className="text-cyan-500 mt-0.5">•</span>ISIN-to-stock-code translation via OpenFIGI</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Collected Data */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-teal-500/10 hover:border-teal-500/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => navigate('/collected-data')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-teal-500/10 rounded-xl"><Database className="h-5 w-5 text-teal-600 dark:text-teal-400" /></div>
                    <div><CardTitle className="text-base">Collected Data</CardTitle><CardDescription className="text-xs">Search history & AI research</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-teal-500 mt-0.5">•</span>AI-powered web search with structured results</li>
                    <li className="flex items-start gap-2"><span className="text-teal-500 mt-0.5">•</span>Search history with source URLs & metadata</li>
                    <li className="flex items-start gap-2"><span className="text-teal-500 mt-0.5">•</span>Multi-model AI responses (Gemini, GPT)</li>
                    <li className="flex items-start gap-2"><span className="text-teal-500 mt-0.5">•</span>Persistent cloud storage of all research</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Hedge Protection */}
              <Card className="cursor-pointer bg-card/80 backdrop-blur-sm border-emerald-500/10 hover:border-emerald-500/30 hover:shadow-lg hover:scale-[1.02] transition-all duration-300" onClick={() => navigate('/hedge-protection')}>
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-emerald-500/10 rounded-xl"><ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" /></div>
                    <div><CardTitle className="text-base">Hedge Protection</CardTitle><CardDescription className="text-xs">AI-powered options hedging</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <ul className="text-xs text-muted-foreground space-y-1.5 ml-1">
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">•</span>Multi-LLM analysis (up to 4 AI models in parallel)</li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">•</span>Protective Puts, Collars & Covered Call strategies</li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">•</span>Consensus scoring with quality & consistency metrics</li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">•</span>Actionable Options Order Book with strike/expiry/premium</li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">•</span>Margin assessment (M275/M163) & BR reduction strategy</li>
                    <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">•</span>Auto-save sessions with restore & history</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Hidden Excel Import Component */}
          <div className="hidden">
            <ExcelImport onImportComplete={loadPositions} />
          </div>
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center gap-3">
            <AddPositionDialog onAddPosition={handleAddPosition} />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={positions.length === 0} className="text-destructive border-destructive/30 hover:bg-destructive/10">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Reset Data
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete all {positions.length} of your imported position{positions.length !== 1 ? 's' : ''} from your account. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleResetData} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete My Data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        
        {/* Portfolio Stats */}
        {analyses.length > 0 && (
          <>
            <div className="mb-8">
              <PortfolioStats {...stats} alertCounts={alertCounts} />
            </div>
            
            {/* Stock Table - All Positions */}
            <div className="mb-8">
              <StockTable 
                analyses={sortedAnalyses}
                onRowClick={scrollToCard}
              />
            </div>
            
            {/* Stock Scorecards - Action Recommendations */}
            <div className="mb-8">
              <StockScorecard analyses={sortedAnalyses} />
            </div>
            
            {/* Fibonacci Retracement Calculator */}
            <div className="mb-8">
              <FibonacciRetracement />
            </div>
            
            {/* Sort Controls for Position Cards */}
            <div className="flex items-center gap-3 mb-6 mt-8">
              <ArrowUpDown className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Sort cards by:</span>
              <Select value={sortBy} onValueChange={(value) => setSortBy(value as SortOption)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="totalValue">Total Value</SelectItem>
                  <SelectItem value="gainLoss">Gain/Loss</SelectItem>
                  <SelectItem value="addedValue">Added Value (CHF)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        
        {/* Positions Grid */}
        {analyses.length === 0 && positions.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-block p-6 bg-muted rounded-full mb-4">
              <TrendingUp className="h-12 w-12 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              No Positions Yet
            </h2>
            <p className="text-muted-foreground mb-6">
              Add your first stock position to start tracking performance
            </p>
            <AddPositionDialog onAddPosition={handleAddPosition} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {sortedAnalyses.map(analysis => (
                <div 
                  key={analysis.id}
                  ref={(el) => (cardRefs.current[analysis.id] = el)}
                  className="transition-all duration-300"
                >
                  <PositionCard
                    analysis={analysis}
                    onDelete={handleDeletePosition}
                  />
                </div>
              ))}
            </div>
            
            {/* Added Value Section - Clear Visual Separation */}
            <div className="mt-40 pt-12 border-t-4 border-primary/20 relative z-0">
              <div className="mb-10">
                <h2 className="text-4xl font-bold text-foreground flex items-center gap-3 mb-3">
                  <TrendingUp className="h-10 w-10 text-primary" />
                  Portfolio Added Value Analysis
                </h2>
                <p className="text-lg text-muted-foreground">
                  Detailed breakdown of stocks contributing positive value to your portfolio
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Summary Statistics Cards */}
                <Card className="shadow-lg">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-2xl">Portfolio Summary</CardTitle>
                    <CardDescription>Key metrics from top performers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(() => {
                        const positiveStocks = sortedAnalyses.filter(a => (a.addedValueChf || 0) > 0);
                        const totalAddedValue = positiveStocks.reduce((sum, a) => sum + (a.addedValueChf || 0), 0);
                        const avgGain = positiveStocks.length > 0 
                          ? (positiveStocks.reduce((sum, a) => sum + ((a.gainPct || 0) * 100), 0) / positiveStocks.length) 
                          : 0;
                        
                        return (
                          <>
                            <div className="flex justify-between items-center p-4 bg-primary/5 rounded-lg">
                              <span className="font-medium text-foreground">Total Profitable Stocks</span>
                              <span className="text-2xl font-bold text-primary">{positiveStocks.length}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-green-500/10 rounded-lg">
                              <span className="font-medium text-foreground">Total Added Value</span>
                              <span className="text-2xl font-bold text-green-600">CHF {totalAddedValue.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center p-4 bg-blue-500/10 rounded-lg">
                              <span className="font-medium text-foreground">Average Gain</span>
                              <span className="text-2xl font-bold text-blue-600">+{avgGain.toFixed(2)}%</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Pie Chart */}
                <div className="h-full">
                  <PortfolioGainsPieChart analyses={analyses} />
                </div>
              </div>
              
              {/* Contributors Table */}
              <Card className="shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-primary" />
                    Contributors
                  </CardTitle>
                  <CardDescription>All stocks with positive added value, color-coded by performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-border">
                          <th className="text-left py-4 px-4 font-bold text-foreground">Rank</th>
                          <th className="text-left py-4 px-4 font-bold text-foreground">Stock</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Added Value (CHF)</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Gain %</th>
                          <th className="text-right py-4 px-4 font-bold text-foreground">Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const contributors = sortedAnalyses
                            .filter(a => (a.addedValueChf || 0) > 0)
                            .sort((a, b) => (b.addedValueChf || 0) - (a.addedValueChf || 0));
                          
                          const total = contributors.length;
                          const topThird = Math.ceil(total / 3);
                          const middleThird = Math.ceil((total * 2) / 3);
                          
                          return contributors.map((analysis, index) => {
                            const currencySymbol = analysis.localCurrency === 'USD' ? '$' : 
                                                   analysis.localCurrency === 'EUR' ? '€' : 'CHF';
                            
                            // Color coding based on rank
                            let rowClass = 'bg-yellow-50/30 dark:bg-yellow-900/10'; // Lowest (yellow)
                            let rankBgClass = 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400';
                            
                            if (index < topThird) {
                              // Best performers (green)
                              rowClass = 'bg-green-50/50 dark:bg-green-900/20';
                              rankBgClass = 'bg-green-500/30 text-green-800 dark:text-green-400';
                            } else if (index < middleThird) {
                              // Middle performers (blue)
                              rowClass = 'bg-blue-50/30 dark:bg-blue-900/10';
                              rankBgClass = 'bg-blue-500/20 text-blue-700 dark:text-blue-400';
                            }
                            
                            return (
                              <tr key={analysis.id} className={`border-b border-border/30 hover:brightness-95 transition-all ${rowClass}`}>
                                <td className="py-4 px-4">
                                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold ${rankBgClass}`}>
                                    {index + 1}
                                  </div>
                                </td>
                                <td className="py-4 px-4">
                                  <div className="font-semibold text-foreground">{analysis.ticker}</div>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="text-lg font-bold text-green-700 dark:text-green-400">
                                    CHF {(analysis.addedValueChf || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 animate-blink">
                                    +{((analysis.gainPct || 0) * 100).toFixed(2)}%
                                  </span>
                                </td>
                                <td className="py-4 px-4 text-right">
                                  <span className="font-medium text-muted-foreground">
                                    {currencySymbol} {(analysis.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-6 flex items-center gap-6 justify-center text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-green-500/30"></div>
                      <span className="text-muted-foreground">Best Performers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-blue-500/20"></div>
                      <span className="text-muted-foreground">Middle Performers</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-yellow-500/20"></div>
                      <span className="text-muted-foreground">Lower Contributors</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
      
      {/* AI Chat Widget */}
      <AIChatWidget />
    </div>
  );
};

export default Index;
